(() => {
  if (window.aaWidthVisualizerGalleryReady) return;
  window.aaWidthVisualizerGalleryReady = true;
  document.documentElement.dataset.aaWidthVisualizerGalleryReady = 'true';
  const visualizerTimers = new WeakMap();

  // Safari/iOS has no scroll anchoring. When the gallery switches to the taller
  // width visualizer, everything below it (including the slider under the finger)
  // jumps down by the height difference. For a short window after each slider
  // interaction, keep the slider's on-screen position fixed by scrolling by the
  // layout-caused shift. Only frames without any page scroll are corrected, so the
  // visitor's own scrolling (and the sticky info column on desktop) is never
  // fought. Native anchoring is paused during the window so Chromium does not
  // double-correct. (2026-09-25)
  const PIN_WINDOW_MS = 1400;
  const pinStates = new WeakMap();
  const docTop = (el) => el.getBoundingClientRect().top + window.scrollY;
  const setPinMode = (on) => {
    [document.documentElement, document.body].forEach((el) => {
      if (!el) return;
      el.style.overflowAnchor = on ? 'none' : '';
      el.style.scrollBehavior = on ? 'auto' : '';
    });
  };
  const pinRange = (range) => {
    const now = performance.now();
    const existing = pinStates.get(range);
    if (existing) {
      existing.until = now + PIN_WINDOW_MS;
      return;
    }
    const state = { top: docTop(range), sy: window.scrollY, until: now + PIN_WINDOW_MS };
    pinStates.set(range, state);
    setPinMode(true);
    const finish = () => {
      pinStates.delete(range);
      setPinMode(false);
    };
    const tick = () => {
      if (!range.isConnected) return finish();
      const top = docTop(range);
      if (window.scrollY !== state.sy) {
        // The page scrolled (visitor, momentum or sticky layout): re-anchor, never fight it.
        state.top = top;
        state.sy = window.scrollY;
      } else {
        const delta = top - state.top;
        if (Math.abs(delta) > 0.5) window.scrollBy(0, delta);
        state.top = top;
        state.sy = window.scrollY;
      }
      if (performance.now() < state.until) requestAnimationFrame(tick);
      else finish();
    };
    requestAnimationFrame(tick);
  };

  const selectVisualizer = (thumb) => {
    const gallery = thumb.closest('hdt-product-media');
    const slider = gallery?.querySelector('.hdt-product-media__main hdt-slider');
    if (!slider) return;
    setTimeout(() => slider.goToIndex(Number(thumb.value), true), 0);
  };

  const selectVisualizerForRange = (range) => {
    const section = range.closest('.shopify-section') || document;
    const thumb = section.querySelector('.aa-width-visualizer-thumb');
    if (thumb) selectVisualizer(thumb);
  };

  // When the mini visualizer inside the slider card is visible (mobile), the
  // gallery no longer jumps to the large visualizer slide on every slider move.
  const miniIsVisible = (range) => {
    const mini = range.closest('aa-width-variant-slider')?.querySelector('[data-aa-width-mini]');
    return Boolean(mini && mini.offsetParent !== null);
  };

  const keepVisualizerSelected = (range) => {
    pinRange(range);
    if (miniIsVisible(range)) return;
    (visualizerTimers.get(range) || []).forEach((timer) => clearTimeout(timer));
    selectVisualizerForRange(range);
    visualizerTimers.set(
      range,
      [80, 220, 500, 900].map((delay) =>
        setTimeout(() => selectVisualizerForRange(range), delay)
      )
    );
  };

  const syncRange = (range, selectVariant = false) => {
    const component = range.closest('aa-width-variant-slider');
    if (!component) return;

    const width = Number(range.value);
    const capacityPerInch = Number(component.dataset.capacityPerInch || 15);
    const capacity = width * capacityPerInch;
    const progress = ((width - Number(range.min)) / (Number(range.max) - Number(range.min))) * 100;

    range.style.setProperty('--aa-progress', `${progress}%`);
    component.querySelectorAll('[data-aa-width], [data-aa-legend-width]').forEach((output) => {
      output.value = String(width);
      if (output.textContent !== String(width)) output.textContent = String(width);
    });
    component.querySelectorAll('[data-aa-capacity]').forEach((output) => {
      output.value = String(capacity);
      if (output.textContent !== String(capacity)) output.textContent = String(capacity);
    });

    if (!selectVariant) return;

    const optionName = component.dataset.optionName || 'Width';
    const isTwoTier = component.hasAttribute('data-two-tier');
    const radios = Array.from(component.querySelectorAll(`input[name="${CSS.escape(optionName)}"]`));
    const radio = radios.find((input) => {
      if (isTwoTier) return Number.parseInt(input.value, 10) === capacity;
      return Number(input.value) === width;
    });
    if (radio && !radio.checked) radio.click();
  };

  const sliderKeys = new Set([
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'End',
    'Home',
    'PageDown',
    'PageUp',
  ]);

  const bindControls = () => {
    document.querySelectorAll('.aa-width-visualizer-thumb:not([data-aa-bound])').forEach((thumb) => {
      thumb.dataset.aaBound = 'true';
      thumb.addEventListener(
        'click',
        () => {
          thumb.dataset.aaVisualizerClicked = 'true';
          selectVisualizer(thumb);
        },
        true
      );
    });

    document.querySelectorAll('[data-aa-range]:not([data-aa-visualizer-switch-bound])').forEach((range) => {
      range.dataset.aaVisualizerSwitchBound = 'true';
      syncRange(range);
      range.addEventListener('pointerdown', () => keepVisualizerSelected(range), true);
      range.addEventListener(
        'input',
        () => {
          syncRange(range, true);
          keepVisualizerSelected(range);
        },
        true
      );
      range.addEventListener('change', () => keepVisualizerSelected(range), true);
      range.addEventListener(
        'keydown',
        (event) => {
          if (sliderKeys.has(event.key)) keepVisualizerSelected(range);
        },
        true
      );
    });

    document.querySelectorAll('[data-aa-range]').forEach((range) => syncRange(range));
  };

  bindControls();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindControls, { once: true });
  }
  document.addEventListener('shopify:section:load', bindControls);
})();
